/**
 * A alçada da margem do orçamento (`TP-064`): a régua SUGERE quem aprova, a pessoa diz quem aprovou (`DEC-005`). A
 * plataforma não decide; só exige que o registro exista para enviar o orçamento à operadora.
 */

export type NivelDeAlcada = 'coordenacao' | 'gerencia' | 'diretoria';

export const NIVEIS_DE_ALCADA: readonly NivelDeAlcada[] = ['coordenacao', 'gerencia', 'diretoria'];

export const ROTULOS_DE_ALCADA: Record<NivelDeAlcada, string> = {
  coordenacao: 'Coordenação',
  gerencia: 'Gerência',
  diretoria: 'Diretoria',
};

/** `sem_dono`: a margem caiu no buraco da régua (a faixa de 10% a 15% do POP não tem dono, de propósito). */
export type SituacaoDaSugestao = 'faixa' | 'sem_dono' | 'sem_regua';

export interface ReguaDeAlcada {
  readonly fonte: string;
  readonly versao: number;
}

export interface SugestaoDeAlcada {
  readonly situacao: SituacaoDaSugestao;
  /** A faixa como o Postgres a escreve (`[7,10]`); `null` fora de `faixa`. */
  readonly faixa: string | null;
  readonly sugerida: NivelDeAlcada | null;
  readonly regua: ReguaDeAlcada | null;
  /** Em `sem_dono`: o texto do catálogo (`ALCADA_BAND_UNOWNED`). */
  readonly mensagem: string | null;
  /** O da requisição: o mesmo do log `alcada.sem_faixa`. A tela o mostra junto do buraco (`NR-41`). */
  readonly correlationId: string | null;
}

export interface RegistroDeAlcada {
  readonly id: string;
  readonly margem: number;
  readonly faixa: string | null;
  readonly sugerida: NivelDeAlcada | null;
  readonly aprovadora: NivelDeAlcada;
  readonly motivo: string | null;
  readonly regua: ReguaDeAlcada | null;
  readonly registradoEm: Date;
  /** `null`: o id não está no cadastro. */
  readonly registradoPorNome: string | null;
}

export interface AlcadaDoCaso {
  /** O Caso está em `orcamento_emitido`: só aí se registra. */
  readonly podeRegistrar: boolean;
  readonly vigente: RegistroDeAlcada | null;
  readonly historico: readonly RegistroDeAlcada[];
}

export function ehNivelDeAlcada(valor: string | null | undefined): valor is NivelDeAlcada {
  return valor === 'coordenacao' || valor === 'gerencia' || valor === 'diretoria';
}

const PERCENTUAL = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 });

export function formatarPercentual(valor: number): string {
  return `${PERCENTUAL.format(valor)}%`;
}

/**
 * A faixa do Postgres em português: `[15,)` → "15% ou mais", `[7,10]` → "de 7% a 10%", `(,7)` → "abaixo de 7%". Forma
 * que não reconhece volta crua: aparecer sinalizado, nunca sumir.
 */
export function rotuloDaFaixa(faixa: string): string {
  const partes = /^([[(])\s*(-?[\d.]*)\s*,\s*(-?[\d.]*)\s*([\])])$/.exec(faixa.trim());
  if (partes === null) return faixa;
  const [, abre = '', de = '', ate = '', fecha = ''] = partes;
  const n = (s: string) => formatarPercentual(Number(s));
  if (de === '' && ate === '') return 'qualquer margem';
  if (de === '') return fecha === ']' ? `até ${n(ate)}` : `abaixo de ${n(ate)}`;
  if (ate === '') return abre === '[' ? `${n(de)} ou mais` : `acima de ${n(de)}`;
  return `de ${n(de)} a ${n(ate)}`;
}

/**
 * Lê a margem digitada: aceita vírgula ou ponto e no máximo duas casas, como a coluna (`numeric(6,2)`). `null` quando
 * não é um percentual.
 */
export function lerMargem(texto: string): number | null {
  const limpo = texto.trim().replace('%', '').replace(',', '.').trim();
  if (!/^-?\d{1,4}(\.\d{1,2})?$/.test(limpo)) return null;
  return Number(limpo);
}
